import labels from "./labels";
import { Employee, Violation, WorkTask } from "../types/ganttChart";

const getViolationStrings = (task: WorkTask): string[] => {
  return task.violations.map((violation) =>
    getViolationMessagesFromViolationName(violation.name),
  );
};

export const resolveEmployeeNames = (
  employeeRefs: string,
  employees: Employee[],
): string => {
  const refs = employeeRefs.split(",");
  const resolvedNames: string[] = [];
  let hasUnresolved = false;

  for (const ref of refs) {
    const employee = employees.find((e) => e.employeeRef === ref.trim());
    if (employee) {
      resolvedNames.push(employee.fullName);
    } else {
      hasUnresolved = true;
    }
  }

  if (hasUnresolved) {
    resolvedNames.push(labels.restrictedEmployeeFallbackName);
  }

  return resolvedNames.join(", ");
};

export const getViolationMessagesFromViolationName = (violation: string) => {
  switch (violation) {
    case "SKILL":
      return labels.violationSkill;
    case "DEPENDENCY":
      return labels.violationDependency;
    case "DEPENDENCY_UNPLANNED":
      return labels.violationDependencyUnplanned;
    case "DEPENDENCY_MIN_DELAY_TIME":
      return labels.violationDependencyTime;
    case "DEPENDENCY_MAX_DELAY_TIME":
      return labels.violationDependencyTime;
    case "DEPENDENCY_MIN_OVERLAP":
      return labels.violationDependency;
    case "DEPENDENCY_SAME_START_TIME":
      return labels.violationDependencyTime;
    case "DEPENDENCY_START_RIGHT_AFTER":
      return labels.violationDependencyTime;
    case "DEPENDENCY_SAME_EMPLOYEE":
      return labels.violationDependencySameEmployee;
    case "OVERDUE":
      return labels.violationOverdue;
    case "OVERTIME":
      return labels.violationOvertime;
    case "TRAVEL_TOTAL_DISTANCE":
      return labels.violationTravelTotalDistance;
    case "TRAVEL_TRIP_DISTANCE":
      return labels.violationTravelTripDistance;
    case "TRAVEL_FIRST_LAST_TRIP_DISTANCE":
      return labels.violationTravelFirstLastTripDistance;
    case "BLOCKED_EMPLOYEE":
      return labels.violationBlockedEmployee;
    case "VALIDATION_TASK_NO_EMPLOYEE_WITH_NECESSARY_SKILL":
      return labels.violationNoEmployeeWithNecessarySkill;
    case "VALIDATION_TASK_INVALID_TIME_WINDOW":
      return labels.violationTaskInvalidTimeWindow;
    case "VALIDATION_TASK_INVALID_LINKEDTASK_DURATION":
      return labels.violationTaskInvalidLinkedTaskDuration;
    case "VALIDATION_CIRCULAR_REFERENCE":
      return labels.violationValidationCircularReference;
    case "VALIDATION_TASK_TOO_LATE_START_COMPARED_TO_EMPLOYEES":
      return labels.violationTaskTooLateStartComparedToEmployees;
    case "VALIDATION_TASK_TOO_EARLY_START_COMPARED_TO_EMPLOYEES":
      return labels.violationTaskTooEarlyStartComparedToEmployees;
    case "VALIDATION_TASK_TOO_EARLY_START_COMPARED_TO_LOCKED_EMPLOYEE":
      return labels.violationTaskTooEarlyStartComparedToLockedEmployee;
    case "VALIDATION_TASK_TOO_LATE_START_COMPARED_TO_LOCKED_EMPLOYEE":
      return labels.violationTaskTooLateStartComparedToLockedEmployee;
    case "VALIDATION_NO_EMPLOYEES":
      return labels.violationNoEmployees;
    case "VALIDATION_TASK_EXPANDED_TIME_WINDOW_NOT_ENCLOSE_PREFERRED_TIME_WINDOW":
      return labels.violationTaskExpandedTimeWindowNotEnclosePreferredTimeWindow;
    case "VALIDATION_TASKS_INVALID_MIN_OR_MAX_DURATION":
      return labels.violationTasksInvalidMinOrMaxDuration;
    case "EMPLOYEES_NOT_WORKING":
      return labels.violationEmployeesNotWorking;
    case "TASK_OUTSIDE_WORK_TIME":
      return labels.violationTaskOutsideWorkTime;
    case "CONCERN_CATEGORY_EMPLOYEE":
      return labels.violationConcernCategoryEmployee;
    case "RESTRICTED_EMPLOYEE":
      return labels.violationRestrictedEmployee(labels.restrictedEmployeeFallbackName);
    default:
      return violation;
  }
};

export const getTransportationViolationMessagesFromViolationName = (
  violation: Violation,
) => {
  switch (violation.name) {
    case "TRAVEL_TOTAL_DISTANCE":
      return labels.violationOfTotalTravelDistance;
    case "TRAVEL_TRIP_DISTANCE":
      return labels.violationOfTravelTripDistance;
    case "TRAVEL_FIRST_LAST_TRIP_DISTANCE":
      return labels.violationOfFirstLastTripDistance;
  }
};

export class RuleConcordanceMessageFactory {
  /**
   * Loops through the task dependencies and violations and generates the requisite messages
   */
  static getMessages = (
    task: WorkTask,
    includeViolations: boolean = true,
  ): string[] => {
    // test if mapping to both dependent and prerequisite

    const messages: string[] = [];

    for (const dependency of task.taskDependencies) {
      if (dependency.maxDelayTime && dependency.maxDelayAnchor)
        messages.push(
          labels.maxDelayTimeViolationMessage(
            dependency.maxDelayTime.toString(),
            dependency.maxDelayAnchor as "START" | "END",
          ),
        );
      if (dependency.minDelayTime && dependency.minDelayAnchor)
        messages.push(
          labels.minDelayTimeViolationMessage(
            dependency.minDelayTime.toString(),
            dependency.minDelayAnchor as "START" | "END",
          ),
        );

      if (dependency.minOverlap) {
        messages.push(
          labels.dependencyMinOverlapViolationMessage(
            dependency.minOverlap.toString(),
          ),
        );
      }

      if (dependency.sameEmployee)
        messages.push(labels.sameEmployeeViolationMessage);

      if (dependency.startRightAfter)
        messages.push(labels.startRightAfterViolationMessage);

      if (dependency.sameStartTime)
        messages.push(labels.sameStartViolationMessage);
    }

    return messages;
  };

  // Deprecated.
  // getViolationMessages = (task: WorkTask): string[] => {
  //   // Get any violation messages if necessary
  //   const violationMessages = task.violations
  //     .filter(
  //       (violation) =>
  //         // Filter out min overlap violations since this is handled by the minOverlap parameter in the dependency object
  //         "name" in violation && violation.name !== "DEPENDENCY_MIN_OVERLAP"
  //     )
  //     .map((violation) => {
  //       switch (violation.name) {
  //         case "SKILL":
  //           return labels.violationSkill;
  //         case "OVERDUE":
  //           return labels.violationOverdue;
  //         case "OVERTIME":
  //           return labels.violationOvertime;
  //         default:
  //           return labels.unspecifiedViolationMessage;
  //       }
  //     });

  //   return violationMessages;
  // };
}

export default getViolationStrings;
